import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { Input } from '$features/pets/forms/shared/types'
import { EditMedicalDetailsContent } from './content'
import { EditMedicalDetailsControls } from './controls'

export { Input }

export const editMedicalDetailsSchema = z.object({
  [Input.MicrochipNumber]: z.string()
    .max(255, { message: 'Microchip number must be less than 255 characters.' })
    .optional(),
  [Input.Weight]: z.number()
    .min(0, { message: 'Weight must be greater than 0.' })
    .max(1000, { message: 'Weight must be less than 1000.' })
    .or(z.nan())
    .optional(),
  [Input.Vaccinated]: z.boolean().optional(),
})

export type EditMedicalDetailsSchema = z.infer<typeof editMedicalDetailsSchema>

type EditMedicalDetailsFormContextType = {
  onSubmit: (values: EditMedicalDetailsSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const EditMedicalDetailsFormContext = createContext<EditMedicalDetailsFormContextType | undefined>(undefined)

type EditMedicalDetailsFormProps = EditMedicalDetailsFormContextType & {
  children?: ReactNode
  defaultValues?: Partial<EditMedicalDetailsSchema>
}

export function EditMedicalDetailsForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditMedicalDetailsFormProps) {
  const context: EditMedicalDetailsFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditMedicalDetailsFormContext.Provider value={context}>
      <Form<EditMedicalDetailsSchema, typeof editMedicalDetailsSchema>
        schema={editMedicalDetailsSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditMedicalDetailsFormContext.Provider>
  )
}

export function useEditMedicalDetailsForm() {
  const context = useContext(EditMedicalDetailsFormContext)
  if (context === undefined) {
    throw new Error('useEditMedicalDetailsForm must be used within an EditMedicalDetailsFormProvider')
  }
  return context
}

// Compound components
EditMedicalDetailsForm.Content = EditMedicalDetailsContent
EditMedicalDetailsForm.Controls = EditMedicalDetailsControls
